import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Info, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { SortableTableHead, getNextSort, type SortDirection } from "@/components/shared/SortableTableHead";
import { TableHeadWithTooltip } from "@/components/shared/TableHeadWithTooltip";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { StickyToolbarProvider } from "@/hooks/useStickyToolbar";
import { TabHeader } from "@/components/shared/TabHeader";
import {
  NestedDrawerContainer,
  NestedDrawerTableHeaderRow,
  NestedDrawerTableBodyRow,
  expandedParentRowClassName,
} from "@/components/shared/NestedDrawerTable";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import type { BiCategoryRow } from "@/resources/brand-intelligence";
import { brandIntelligenceKeys, useGetBrandIntelligenceCategoryProducts } from "@/resources/brand-intelligence/brand-intelligence.api";
import { useAddHeroProducts, describeAddHeroProductsResult, useGetHeroProductList } from "@/resources/product/product.api";
import { handleApiError } from "@/utils";
import { RoutePath } from "@/routes";
import { RevenueTreemap, type TreemapView } from "./RevenueTreemap";
import { BI_TOOLTIPS } from "./tooltips";

const cellCls = "px-4 py-3";
const fmtUsd = (n: number): string => {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};
const fmtRevShare = (n: number | null | undefined) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);
const fmtGrowth = (n: number | null | undefined) => (n == null ? "—" : `${n >= 0 ? "+" : ""}${(n * 100).toFixed(1)}%`);
const fmtSov = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(2)}%`);

const growthColor = (status: string | null) =>
  status === "Outpacing" ? "text-trend-positive" : status === "Lagging" ? "text-trend-negative" : "text-primary";

// Section 2.7 health tier badge (Status column).
function HealthBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const cls = status === "Strong" ? "text-trend-positive" : status === "Needs work" ? "text-trend-negative" : "text-status-warning-strong";
  return <span className={cn("text-xs font-semibold", cls)}>{status}</span>;
}

// Section 2.5 Brand Visibility ring (0-100 Content Scorecard rollup).
function VisibilityRing({ score, partial }: { score: number | null; partial: boolean }) {
  if (score == null) return <span className="text-muted-foreground">—</span>;
  const bg = score >= 75 ? "bg-status-success-soft" : score >= 60 ? "bg-status-warning-soft" : "bg-status-error-soft";
  return (
    <span
      className={cn("inline-flex items-center justify-center h-8 w-8 rounded-full text-xs font-semibold tabular-nums text-foreground", bg)}
      title={partial ? "Reflects only the brand's Signal-added ASINs, a partial subset of the category's full catalog." : undefined}
    >
      {score.toFixed(0)}
      {partial && "*"}
    </span>
  );
}

// Section 3.3: expanded product rows (lazy). Signal-feature columns (Search Vol / Pg-1 /
// AI Visibility / A/B Tests / Thematic) are blank until the ASIN is added to Signal.
// AUT-311: an already-added product links to its Signal product-detail page; if we can't
// resolve its hero product id yet, fall back to a plain "Added" label.
function AddedCell({ href }: { href?: string }) {
  return href ? (
    <Link to={href} className="text-xs text-primary hover:underline">View</Link>
  ) : (
    <span className="text-xs text-muted-foreground">Added</span>
  );
}

function CategoryProductRows({
  brandId,
  leafCategoryId,
  selected,
  onToggle,
  onAdd,
  adding,
  productHref,
}: {
  brandId: number;
  leafCategoryId: string;
  selected: Set<string>;
  onToggle: (asin: string) => void;
  onAdd: (asin: string) => void;
  adding: boolean;
  productHref: (asin: string) => string | undefined;
}) {
  const { data, isLoading } = useGetBrandIntelligenceCategoryProducts(brandId, leafCategoryId);
  if (isLoading) return <div className="px-4 py-4 text-xs text-muted-foreground">Loading products…</div>;
  if (!data || data.products.length === 0) return <div className="px-4 py-4 text-xs text-muted-foreground">No products found for this category.</div>;
  const dash = <span className="text-muted-foreground">—</span>;
  return (
    <Table>
      <TableHeader>
        <NestedDrawerTableHeaderRow>
          <TableHead className={cellCls}>Product</TableHead>
          <TableHeadWithTooltip tooltip={BI_TOOLTIPS.organicSov} className={cn(cellCls, "text-right")}>SOV</TableHeadWithTooltip>
          <TableHead className={cn(cellCls, "text-right")}>L12 Revenue</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>Price</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>Amazon AI Score</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>Search Vol.</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>Pg-1 Rate</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>AI Visibility</TableHead>
          <TableHead className={cn(cellCls, "text-right")}>A/B Tests</TableHead>
          <TableHead className={cellCls}>Thematic Groupings</TableHead>
          <TableHead className={cn(cellCls, "w-[1%]")} />
        </NestedDrawerTableHeaderRow>
      </TableHeader>
      <TableBody>
        {data.products.map((p) => (
          <NestedDrawerTableBodyRow key={p.asin}>
            <TableCell className={cellCls}>
              <div className="flex items-center gap-2">
                <Checkbox checked={selected.has(p.asin)} onCheckedChange={() => onToggle(p.asin)} onClick={(e) => e.stopPropagation()} />
                <ProductTableCell name={p.title || p.asin} asin={p.asin} imageUrl={p.image_url} size="lg" wrapName asinLinkToAmazon />
              </div>
            </TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums")}>
              {p.product_sov == null ? dash : (
                <span title={p.product_sov_estimated ? "Estimated (this product doesn't rank organically yet)" : undefined}>
                  {p.product_sov.toFixed(1)}%{p.product_sov_estimated && <span className="text-muted-foreground/60">*</span>}
                </span>
              )}
            </TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums text-muted-foreground")}>{fmtUsd(p.revenue)}</TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums text-muted-foreground")}>{p.price == null ? dash : `$${p.price.toFixed(2)}`}</TableCell>
            {/* Signal-feature columns: blank until the ASIN is added to Signal (§3.3);
                AUT-313 populates them from the product's live Signal data once added.
                AI Visibility stays blank -- there is no per-product AI-visibility metric. */}
            <TableCell className={cn(cellCls, "text-right tabular-nums")}>{p.added && p.content_scorecard != null ? p.content_scorecard : dash}</TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums")}>{p.added && p.search_volume != null ? p.search_volume.toLocaleString() : dash}</TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums")}>{p.added && p.keywords_pg1_count != null ? p.keywords_pg1_count : dash}</TableCell>
            <TableCell className={cn(cellCls, "text-right")}>{dash}</TableCell>
            <TableCell className={cn(cellCls, "text-right tabular-nums")}>{p.added && p.ab_tests_running != null ? p.ab_tests_running : dash}</TableCell>
            <TableCell className={cellCls}>{p.added && p.themes_count != null ? p.themes_count : dash}</TableCell>
            <TableCell className={cellCls}>
              {p.added ? (
                <AddedCell href={productHref(p.asin)} />
              ) : (
                <button type="button" disabled={adding} onClick={() => onAdd(p.asin)} className="text-xs text-primary hover:underline disabled:opacity-50" title={BI_TOOLTIPS.productSignalColumns}>Add to Signal</button>
              )}
            </TableCell>
          </NestedDrawerTableBodyRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ExpandedCategory({
  brandId,
  category,
  selectedProducts,
  onToggleProduct,
  onAddToSignal,
  adding,
  productHref,
}: {
  brandId: number;
  category: BiCategoryRow;
  selectedProducts: Set<string>;
  onToggleProduct: (asin: string) => void;
  onAddToSignal: (asin: string) => void;
  adding: boolean;
  productHref: (asin: string) => string | undefined;
}) {
  return (
    <NestedDrawerContainer>
      {category.hero_alert && category.hero_concentration != null && (
        <div className="px-4 pt-3 pb-2">
          <Alert variant="info" className="flex items-start gap-2">
            <Sparkles className="h-4 w-4" />
            <div>
              <AlertTitle>Hero ASIN concentration</AlertTitle>
              <AlertDescription>
                An estimated {(category.hero_concentration * 100).toFixed(0)}% of this category's brand revenue flows through a
                single product — a concentration worth monitoring.
              </AlertDescription>
            </div>
          </Alert>
        </div>
      )}
      <CategoryProductRows brandId={brandId} leafCategoryId={category.leaf_category_id} selected={selectedProducts} onToggle={onToggleProduct} onAdd={onAddToSignal} adding={adding} productHref={productHref} />
    </NestedDrawerContainer>
  );
}

type SortField = "name" | "brandRev" | "catRev" | "revShare" | "catGrowth" | "brandAsins" | "paidSov" | "organicSov" | "sov" | "visibility";

export function CategoriesTab({ brandId, categories }: { brandId: number; categories: BiCategoryRow[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [view, setView] = useState<TreemapView>("brand");
  const [selectedTiles, setSelectedTiles] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const queryClient = useQueryClient();
  const { mutateAsync: addHeroProductsAsync, isPending: isAdding } = useAddHeroProducts();
  const { data: heroProducts } = useGetHeroProductList(brandId);

  // AUT-311: resolve an already-added ASIN to its Signal product-detail path via the hero list.
  const heroIdByAsin = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of heroProducts ?? []) m.set(h.asin.trim().toUpperCase(), h.id);
    return m;
  }, [heroProducts]);
  const productHref = (asin: string): string | undefined => {
    const id = heroIdByAsin.get(asin.trim().toUpperCase());
    return id != null ? `${RoutePath.Products}/${id}` : undefined;
  };

  // AUT-305: add the selected (or a single) ASIN to Signal as hero product(s), reusing the
  // app-wide add-hero-products endpoint (reports added vs already-added). Refresh the category
  // product rows so their "Add to Signal" flips to "Added". Returns success for callers that
  // clear their selection only on a clean add.
  const handleAddToSignal = async (asins: string[]): Promise<boolean> => {
    if (asins.length === 0) return false;
    try {
      const result = await addHeroProductsAsync({ brandId, asins });
      const { title, description } = describeAddHeroProductsResult(result);
      toast.success(title, { description });
      queryClient.invalidateQueries({ queryKey: brandIntelligenceKeys.categoryProducts(brandId) });
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  };

  const handleSort = (field: SortField) => {
    const next = getNextSort(sortField, sortDir, field);
    setSortField(next.field);
    setSortDir(next.direction);
  };
  const toggleTile = (leaf: string) =>
    setSelectedTiles((prev) => {
      const n = new Set(prev);
      if (n.has(leaf)) n.delete(leaf);
      else n.add(leaf);
      return n;
    });
  const toggleProduct = (asin: string) =>
    setSelectedProducts((prev) => {
      const n = new Set(prev);
      if (n.has(asin)) n.delete(asin);
      else n.add(asin);
      return n;
    });
  // AUT-312: categories expand independently -- expanding one must not collapse the others.
  const toggleExpanded = (leaf: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(leaf)) n.delete(leaf);
      else n.add(leaf);
      return n;
    });

  const statusCounts = useMemo(() => {
    const c = { Strong: 0, Gaps: 0, "Needs work": 0 } as Record<string, number>;
    for (const r of categories) if (r.health_status && c[r.health_status] != null) c[r.health_status]++;
    return c;
  }, [categories]);

  const filtered = useMemo(() => {
    let d = [...categories];
    const q = search.trim().toLowerCase();
    if (q) d = d.filter((r) => r.name.toLowerCase().includes(q));
    if (statusFilter !== "all") d = d.filter((r) => r.health_status === statusFilter);
    if (selectedTiles.size > 0) d = d.filter((r) => selectedTiles.has(r.leaf_category_id));
    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1;
      const key: Record<SortField, (r: BiCategoryRow) => number | string> = {
        name: (r) => r.name,
        brandRev: (r) => r.brand_revenue,
        catRev: (r) => r.category_revenue,
        revShare: (r) => r.rev_share ?? -1,
        catGrowth: (r) => r.category_growth ?? -1e9,
        brandAsins: (r) => r.brand_asins,
        paidSov: (r) => r.paid_sov ?? -1,
        organicSov: (r) => r.organic_sov ?? -1,
        sov: (r) => r.total_sov ?? -1,
        visibility: (r) => r.brand_visibility ?? -1,
      };
      d.sort((a, b) => {
        const va = key[sortField](a);
        const vb = key[sortField](b);
        return typeof va === "string" ? dir * (va as string).localeCompare(vb as string) : dir * ((va as number) - (vb as number));
      });
    }
    return d;
  }, [categories, search, statusFilter, selectedTiles, sortField, sortDir]);

  const tileNames = useMemo(() => categories.filter((c) => selectedTiles.has(c.leaf_category_id)).map((c) => c.name), [categories, selectedTiles]);

  if (categories.length === 0) {
    return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">No categories found for this brand yet.</div>;
  }

  return (
    <StickyToolbarProvider>
    <div className="space-y-6">
      {/* Treemap block */}
      <div className="space-y-4">
        <TabHeader
          title="Revenue by Category"
          description="Click any tile to filter the table below"
          showDescription
          titleAdornment={
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger><Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" /></TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Tile size reflects L12M revenue; color reflects category health. Toggle between the brand's revenue in each category and the total category revenue on Amazon.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />

        <div className="flex items-center gap-3 flex-wrap">
          <SearchFilterBar
            search={search}
            onSearchChange={setSearch}
            placeholder="Search categories…"
            searchWidth="w-72"
            filterColumns={[
              {
                id: "status",
                label: "Status",
                options: [
                  { value: "Strong", label: `Strong (${statusCounts.Strong})` },
                  { value: "Gaps", label: `Gaps (${statusCounts.Gaps})` },
                  { value: "Needs work", label: `Needs work (${statusCounts["Needs work"]})` },
                ],
              },
            ]}
            activeFilters={{ status: statusFilter === "all" ? [] : [statusFilter] }}
            onFiltersChange={(f) => setStatusFilter(f.status?.[0] ?? "all")}
          />
          <div className="ml-auto text-sm text-muted-foreground">{filtered.length} of {categories.length} categories</div>
          <Tabs value={view} onValueChange={(v) => setView(v as TreemapView)}>
            <TabsList variant="segmented">
              <TabsTrigger variant="segmented" value="brand">Brand revenue</TabsTrigger>
              <TabsTrigger variant="segmented" value="category">Category revenue</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <RevenueTreemap categories={categories} view={view} selected={selectedTiles} onTile={toggleTile} />

        {selectedTiles.size > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Filtered by tile{selectedTiles.size > 1 ? "s" : ""}: <span className="font-medium text-foreground">{tileNames.join(", ")}</span>
            <button type="button" className="text-primary hover:underline" onClick={() => setSelectedTiles(new Set())}>Clear</button>
          </div>
        )}
      </div>

      <BulkActionBar count={selectedProducts.size} entityLabel="product" onClear={() => setSelectedProducts(new Set())} sticky>
        <button
          type="button"
          disabled={isAdding}
          onClick={async () => {
            if (await handleAddToSignal([...selectedProducts])) setSelectedProducts(new Set());
          }}
          className="text-xs text-primary hover:underline disabled:opacity-50"
        >
          Add to Signal
        </button>
      </BulkActionBar>

      {/* Table */}
      <div className="border-y border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <SortableTableHead field="name" activeField={sortField} direction={sortDir} onSort={handleSort} className={cellCls}>Category</SortableTableHead>
              <SortableTableHead field="brandRev" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="The brand's last-12-month revenue in this category." className={cn(cellCls, "text-right")}>Brand L12 Rev</SortableTableHead>
              <SortableTableHead field="catRev" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="Total last-12-month revenue for the category on Amazon." className={cn(cellCls, "text-right")}>Category L12 Rev</SortableTableHead>
              <SortableTableHead field="revShare" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="Brand revenue as a share of total category revenue." className={cn(cellCls, "text-right")}>Rev Share</SortableTableHead>
              <SortableTableHead field="catGrowth" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="Category revenue growth vs. prior 12 months, and the brand's pace relative to it." className={cn(cellCls, "text-right")}>Cat. Growth</SortableTableHead>
              <SortableTableHead field="brandAsins" activeField={sortField} direction={sortDir} onSort={handleSort} className={cn(cellCls, "text-right")}>Brand ASINs</SortableTableHead>
              <SortableTableHead field="paidSov" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip={BI_TOOLTIPS.paidSov} className={cn(cellCls, "text-right")}>Paid SOV</SortableTableHead>
              <SortableTableHead field="organicSov" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip={BI_TOOLTIPS.organicSov} className={cn(cellCls, "text-right")}>Organic SOV</SortableTableHead>
              <SortableTableHead field="sov" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip={BI_TOOLTIPS.totalSov} className={cn(cellCls, "text-right")}>Total SOV</SortableTableHead>
              <TableHeadWithTooltip tooltip={BI_TOOLTIPS.brandVisibility} className={cn(cellCls, "text-center")}>Brand Visibility</TableHeadWithTooltip>
              <TableHeadWithTooltip tooltip={BI_TOOLTIPS.status} className={cellCls}>Status</TableHeadWithTooltip>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className={cn(cellCls, "text-center text-muted-foreground py-8")}>No categories match the current filters</TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => {
                const isOpen = expanded.has(c.leaf_category_id);
                const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
                const revShareHi = (c.rev_share ?? 0) >= 0.05;
                const sovUp = c.sov_trend === "up";
                const sovDown = c.sov_trend === "down";
                return (
                  <Fragment key={c.leaf_category_id}>
                    <TableRow className={cn("cursor-pointer", isOpen && expandedParentRowClassName)} onClick={() => toggleExpanded(c.leaf_category_id)}>
                      <TableCell className={cn(cellCls, "text-muted-foreground")}><ChevronIcon className="h-4 w-4" /></TableCell>
                      <TableCell className={cn(cellCls, "font-medium")}>
                        <span className="flex items-center gap-2">
                          {c.name}
                          {c.hero_alert && (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger><Sparkles className="h-3.5 w-3.5 text-primary" /></TooltipTrigger>
                                <TooltipContent>Hero ASIN concentration — expand this row.</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums")}>{fmtUsd(c.brand_revenue)}</TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums text-muted-foreground")}>{fmtUsd(c.category_revenue)}</TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums", revShareHi ? "text-trend-positive" : "text-trend-negative")}>{fmtRevShare(c.rev_share)}</TableCell>
                      <TableCell className={cellCls}>
                        <div className="flex flex-col items-end">
                          <span className="tabular-nums text-foreground">{fmtGrowth(c.category_growth)}</span>
                          {c.growth_status && (
                            <span className={cn("text-xs flex items-center gap-0.5", growthColor(c.growth_status))}>
                              {c.growth_status === "Outpacing" && <TrendingUp className="h-3 w-3" />}
                              {c.growth_status === "Lagging" && <TrendingDown className="h-3 w-3" />}
                              {c.growth_status}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums")}>{c.brand_asins}</TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums")}>{fmtSov(c.paid_sov)}</TableCell>
                      <TableCell className={cn(cellCls, "text-right tabular-nums")}>{fmtSov(c.organic_sov)}</TableCell>
                      <TableCell className={cn(cellCls, "text-right")}>
                        <span className={cn("inline-flex items-center justify-end gap-1 tabular-nums", sovUp ? "text-trend-positive" : sovDown ? "text-trend-negative" : "text-foreground")}>
                          {sovUp && <TrendingUp className="h-3 w-3" />}
                          {sovDown && <TrendingDown className="h-3 w-3" />}
                          {fmtSov(c.total_sov)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(cellCls, "text-center")}><VisibilityRing score={c.brand_visibility} partial={c.brand_visibility_partial} /></TableCell>
                      <TableCell className={cellCls}><HealthBadge status={c.health_status} /></TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={12} className="p-0">
                          <ExpandedCategory brandId={brandId} category={c} selectedProducts={selectedProducts} onToggleProduct={toggleProduct} onAddToSignal={(asin) => { void handleAddToSignal([asin]); }} adding={isAdding} productHref={productHref} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
    </StickyToolbarProvider>
  );
}
